import { CueNote } from "../types";
import { NOTES_KEY } from "../state/notes-store";
import { createPacket, listPackets, setCurrentPacket, updatePacketNotes, getCurrentPacketId } from "./packets";

/**
 * First-run helper:
 * - If IndexedDB has no packets but localStorage has notes, create "My Notes" packet.
 * - If there are packets but no current packet id, set the most-recent as current.
 * - Always mirror the current packet's notes back to localStorage for routing.
 */
export async function runMigrationsAndMirror(): Promise<void> {
  const packets = await listPackets();
  const lsRaw = localStorage.getItem(NOTES_KEY);
  const lsNotes: CueNote[] = safeParseArray(lsRaw);

  if (packets.length === 0 && lsNotes.length > 0) {
    const p = await createPacket("My Notes", lsNotes);
    await setCurrentPacket(p.id);
  }

  // Ensure a current packet exists
  let currentId = await getCurrentPacketId();
  if (!currentId) {
    if (packets.length > 0) {
      // pick the most recently updated as default
      const latest = [...packets].sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))[0];
      await setCurrentPacket(latest.id);
      currentId = latest.id;
    }
  }

  // Mirror current packet notes into localStorage for RootRedirect compatibility
  if (currentId) {
    const refreshed = await updatePacketNotes(currentId, undefined, { mirrorOnly: true });
    if (refreshed) {
      try {
        localStorage.setItem(NOTES_KEY, JSON.stringify(refreshed.notes));
      } catch {}
    }
  }
}

function safeParseArray(raw: string | null): CueNote[] {
  try {
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr as CueNote[] : [];
  } catch {
    return [];
  }
}
